import { fabric } from "fabric";
import {
    coordsAreAxisAlignedQuad,
    quadCoordsToXYWH,
    normalizePolygonAfterTransform,
} from "@/bbox_canvas/geometry";
import {
    annotationFillRgba,
    resolvedColorValues,
    annotationFillFor,
    annotationGlowFor,
    annotationGlowShadow,
    labelTextFill,
} from "@/bbox_canvas/styles";

/**
 * Building, mutating and reading back annotations (boxes, polygons, points),
 * their label chips and the Fabric event listeners wired onto each one.
 * Mixed into BboxCanvas.prototype.
 */
export default {
    modifyObject(obj, marginLeft, marginTop) {
        let this2 = this;

        if (obj.is_polygon) {
            normalizePolygonAfterTransform(obj);
        }

        let left = obj.left + marginLeft;
        let top = obj.top + marginTop;

        obj.set({
            left: left,
            top: top
        })

        const coords = this2.getAnnotationCoords(obj);
        if (obj.annotation) {
            obj.annotation.coords = coords;
        }
        window.dispatchEvent(new CustomEvent("update_coords", {
            detail: {
                coords: coords,
                id: obj.annotation_id,
            }
        }));
        if(obj.text) {
            obj.text.set({
                fontSize: this.labelFontSize(),
                lineHeight: this.labelLineHeight,
            });
            if (obj.text.type === 'textbox') {
                this._layoutBoxLabelText(obj.text);
            }
            obj.text.set({
                left: left,
                top: top - obj.text.calcTextHeight() - 2,
            });
        }
        if(obj.inner_point) {
            obj.inner_point.set({
                left: left + obj.radius - this2.inner_point_radius,
                top: top + obj.radius - this2.inner_point_radius,
            })
        }
        if(obj.is_point) {
            obj.x1 = left + obj.radius;
            obj.y1 = top + obj.radius;
            if(obj.inner_point) {
                obj.inner_point.x1 = obj.x1;
                obj.inner_point.y1 = obj.y1;
            }
        }
    },

    moveObject(obj, marginLeft, marginTop) {
        let left = obj.left + marginLeft;
        let top = obj.top + marginTop;

        if(obj.text) {
            obj.text.set({
                fontSize: this.labelFontSize(),
                lineHeight: this.labelLineHeight,
            });
            if (obj.text.type === 'textbox') {
                this._layoutBoxLabelText(obj.text);
            }
            obj.text.set({
                left: left,
                top: top - obj.text.calcTextHeight() - 2,
            });
        }
        if(obj.inner_point) {
            obj.inner_point.set({
                left: left + obj.width / 2 - this.inner_point_radius,
                top: top + obj.width / 2 - this.inner_point_radius,
            })
        }
        if(obj.is_point) {
            obj.x1 = left + obj.radius;
            obj.y1 = top + obj.radius;
            if(obj.inner_point) {
                obj.inner_point.x1 = obj.x1;
                obj.inner_point.y1 = obj.y1;
            }
        }
    },

    addAnnotation(annotation, newly_added) {

        if(!this.draw_annotations) {
            return;
        }

        annotation = JSON.parse(JSON.stringify(annotation));

        let type = annotation.type;
        let color_values = resolvedColorValues(annotation);
        let label = annotation.label;

        let color = new fabric.Color('rgb(' + color_values[0] + ',' + color_values[1] + ',' + color_values[2] + ')');

        let activeObject = this.canvas.getActiveObject()
        if (activeObject && activeObject.category && activeObject.category !== label  && activeObject.type === type) {
            activeObject.category = label;
            activeObject.stroke = color.toRgb();
            activeObject.fill = annotationFillRgba(color_values);
            activeObject.shadow = annotationGlowShadow(color_values, Math.max(this.canvas.getZoom(), 0.01));
            activeObject.text.text = label;
            activeObject.text.backgroundColor = color.toRgb();
            activeObject.text.fill = labelTextFill(color_values);
            activeObject.text.set({ lineHeight: this.labelLineHeight });
            this._layoutBoxLabelText(activeObject.text);
            this.requestRenderAll();
            return;
        }

        if(type === 'box') {
            this.addAnnotationBox(annotation, newly_added)
        }
        else if(type === 'polygon') {
            if (coordsAreAxisAlignedQuad(annotation.coords)) {
                const rectAnnotation = Object.assign({}, annotation, {
                    type: 'box',
                    coords: quadCoordsToXYWH(annotation.coords),
                });
                this.addAnnotationBox(rectAnnotation, newly_added);
            } else {
                this.addAnnotationPolygon(annotation, newly_added);
            }
        }
        else if(type === 'point') {
            this.addAnnotationPoint(annotation, newly_added)
        }

        if (this.placementMode) {
            this._setAnnotationsPlacementPassthrough(true);
        }

        this.requestRenderAll();
    },

    /**
     * fabric.IText / fabric.Text initDimensions() overwrites width to calcTextWidth(), so extra
     * horizontal padding never sticks. fabric.Textbox keeps a fixed width unless content is wider.
     */
    _layoutBoxLabelText(textObj) {
        if (!textObj || textObj.type !== 'textbox') {
            return;
        }
        textObj.set({ textAlign: 'center' });
        textObj.initDimensions();
        const tw = textObj.calcTextWidth();
        const padX = typeof this.labelPadXForZoom === 'function'
            ? this.labelPadXForZoom()
            : this.labelPadX;
        textObj.set({ width: Math.max(tw + 2 * padX, 4) });
        textObj.initDimensions();
        if (typeof textObj.setCoords === 'function') {
            textObj.setCoords();
        }
    },

    addAnnotationBox(annotation, newly_added) {
        let coords = annotation.coords;
        let x1 = coords[0] * this.canvasWidth;
        let y1 = coords[1] * this.canvasHeight;
        let x2 = x1 + coords[2] * this.canvasWidth;
        let y2 = y1 + coords[3] * this.canvasHeight;

        if(newly_added && !annotation.skip_zoom) {
            let zoom = this.canvas.getZoom()
            x1 = x1/zoom-this.canvas.viewportTransform[4]/zoom
            y1 = y1/zoom-this.canvas.viewportTransform[5]/zoom
            x2 = x2/zoom-this.canvas.viewportTransform[4]/zoom
            y2 = y2/zoom-this.canvas.viewportTransform[5]/zoom
        }

        let label = annotation.label;
        let conf = annotation.conf;

        let color_values = resolvedColorValues(annotation);
        let color = new fabric.Color('rgb(' + color_values[0] + ',' + color_values[1] + ',' + color_values[2] + ')');

        const zoom = Math.max(this.canvas.getZoom(), 0.01);
        let box = new fabric.Rect({
            fill: annotationFillFor(annotation, color_values),
            left: x1 - this.boxBorder,
            top: y1 - this.boxBorder,
            height: y2 - y1,
            width: x2 - x1,
            scaleX: 1,
            scaleY: 1,
            strokeWidth: this.annotationStrokeWidth(),
            stroke: color.toRgb(),
            strokeUniform: true,
            lockUniScaling: false,
            opacity: 1,
            shadow: annotationGlowFor(annotation, color_values, zoom),
            annotation_id: annotation.id,
            annotation: annotation,
            selectable: !this.read_only,
            cornerSize: 14,
            touchCornerSize: 28,
            transparentCorners: false,
        });

        let textLabel = label
        if(conf != 1) {
            textLabel = textLabel + ' ' + conf
        }
        var text = new fabric.Textbox(textLabel, {
            fontSize: this.labelFontSize(),
            lineHeight: this.labelLineHeight,
            fontFamily: 'Arial, Helvetica, sans-serif',
            fill: labelTextFill(color_values),
            backgroundColor: color.toRgb(),
            lockUniScaling: false,
            selectable: false,
            evented: false,
            opacity: 1,
            annotation_id: annotation.id,
            text_box: true,
            width: Math.max(this.canvasWidth, 400),
            shadow: new fabric.Shadow({
                color: 'rgba(0,0,0,0.45)',
                blur: 3 / zoom,
                offsetX: 0,
                offsetY: 1 / zoom,
            }),
        });

        this._layoutBoxLabelText(text);

        /* Label chip: left edge flush with box */
        text.left = x1 - this.boxBorder;
        text.top = y1 - this.boxBorder - text.calcTextHeight() - 2;

        box.text = text;

        box.set('category', label);

        box.setControlsVisibility({
            mtr: false,
        });
        this.ensureDeleteControlOnObject(box);

        this.canvas.add(text);

        this.canvas.add(box);
        let this2 = this;
        if(newly_added) {
             window.dispatchEvent(new CustomEvent("update_coords", {
                detail: {
                    coords: this2.getBoxCoords(box),
                    id: box.annotation_id,
                }
            }));
        }

        this.addEventListerenersForAnnotation(box)
    },

    addAnnotationPolygon(annotation, newly_added) {
        let coords = annotation.coords;
        let points = [];
        for (let i = 0; i < 8; i += 2) {
            points.push({
                x: coords[i] * this.canvasWidth,
                y: coords[i + 1] * this.canvasHeight,
            });
        }

        let label = annotation.label;
        let conf = annotation.conf;
        let color_values = resolvedColorValues(annotation);
        let color = new fabric.Color('rgb(' + color_values[0] + ',' + color_values[1] + ',' + color_values[2] + ')');

        const zoom = Math.max(this.canvas.getZoom(), 0.01);
        let poly = new fabric.Polygon(points, {
            fill: annotationFillFor(annotation, color_values),
            stroke: color.toRgb(),
            strokeWidth: this.annotationStrokeWidth(),
            strokeUniform: true,
            opacity: 1,
            shadow: annotationGlowFor(annotation, color_values, zoom),
            annotation_id: annotation.id,
            annotation: annotation,
            selectable: !this.read_only,
            is_polygon: true,
            cornerSize: 14,
            touchCornerSize: 28,
            transparentCorners: false,
        });

        let textLabel = label;
        if (conf != 1) {
            textLabel = textLabel + ' ' + conf;
        }
        let minX = Math.min(...points.map((p) => p.x));
        let minY = Math.min(...points.map((p) => p.y));
        var text = new fabric.Textbox(textLabel, {
            fontSize: this.labelFontSize(),
            lineHeight: this.labelLineHeight,
            fontFamily: 'Arial, Helvetica, sans-serif',
            fill: labelTextFill(color_values),
            backgroundColor: color.toRgb(),
            lockUniScaling: false,
            selectable: false,
            evented: false,
            opacity: 1,
            annotation_id: annotation.id,
            text_box: true,
            width: Math.max(this.canvasWidth, 400),
        });
        this._layoutBoxLabelText(text);
        text.left = minX - this.boxBorder;
        text.top = minY - this.boxBorder - text.calcTextHeight() - 2;
        poly.text = text;
        poly.set('category', label);
        poly.setControlsVisibility({ mtr: false });
        this.ensureDeleteControlOnObject(poly);
        this.canvas.add(text);
        this.canvas.add(poly);
        let this2 = this;
        if (newly_added) {
            window.dispatchEvent(new CustomEvent("update_coords", {
                detail: {
                    coords: this2.getAnnotationCoords(poly),
                    id: poly.annotation_id,
                }
            }));
        }
        this.addEventListerenersForAnnotation(poly);
    },

    addAnnotationPoint(annotation, newly_added) {
        let coords = annotation.coords;

        let x1 = coords[0] * this.canvasWidth;
        let y1 = coords[1] * this.canvasHeight;

        if(newly_added && !annotation.skip_zoom) {
            let zoom = this.canvas.getZoom()
            x1 = x1/zoom-this.canvas.viewportTransform[4]/zoom
            y1 = y1/zoom-this.canvas.viewportTransform[5]/zoom
        }

        let label = annotation.label;
        let conf = annotation.conf;

        let color_values = resolvedColorValues(annotation);
        let color = new fabric.Color('rgb(' + color_values[0] + ',' + color_values[1] + ',' + color_values[2] + ')');

        let point = new fabric.Circle({
            radius: this.point_radius,
            fill: color.toRgb(),
            left: x1 - this.point_radius,
            top: y1 - this.point_radius,
            x1: x1,
            y1: y1,
            scaleX: 1,
            scaleY: 1,
            strokeWidth: 0,
            stroke: color.toRgb(),
            strokeUniform: true,
            lockUniScaling: false,
            opacity: 0.5,
            annotation_id: annotation.id,
            annotation: annotation,
            hasControls: true,
            selectable: !this.read_only,
            is_point: true,
        });

        let black = new fabric.Color('rgb(' + 0 + ',' + 0 + ',' + 0 + ')');
        let white = new fabric.Color('rgb(' + 255 + ',' + 255 + ',' + 255 + ')');

        let inner_point = new fabric.Circle({
            radius: this.inner_point_radius,
            fill: true,
            left: x1 - this.inner_point_radius,
            top: y1 - this.inner_point_radius,
            x1: x1,
            y1: y1,
            scaleX: 1,
            scaleY: 1,
            strokeWidth: 0,
            stroke: white.toRgb(),
            color: black.toRgb(),
            strokeUniform: true,
            lockUniScaling: false,
            opacity: 0.5,
            hasControls: false,
            selectable: false,
            is_inner_point: true,
        });

        point.set('category', label);
        point.set('inner_point', inner_point);

        let this2 = this;

        point.setControlsVisibility({
            tr: false,
            tl: false,
            bl: false,
            br: false,
            mt: false,
            mtr: false,
            mr: false,
            ml: false,
            mb: false,
        });
        this.ensureDeleteControlOnObject(point);

        let textLabel = label
        if(conf != 1) {
            textLabel = textLabel + ' ' + conf
        }
        var text = new fabric.IText(textLabel, {
            fontSize: this.labelFontSize(),
            backgroundColor: color.toRgb(),
            lockUniScaling: false,
            selectable: false,
            evented: false,
            opacity: 0.0,
            annotation_id: annotation.id,
            text_box: true,
        });

        text.left = x1 - this.point_radius
        text.top = y1 - this.point_radius * 2

        point.text = text;

        this.canvas.add(inner_point);
        this.canvas.add(point);
        this.canvas.add(text);

        if(newly_added) {
             window.dispatchEvent(new CustomEvent("update_coords", {
                detail: {
                    coords: this2.getBoxCoords(point),
                    id: point.annotation_id,
                }
            }));
        }

        this.addEventListerenersForAnnotation(point)
    },

    getBoxCoords(annotation) {
        return this.getAnnotationCoords(annotation);
    },

    getAnnotationCoords(annotation) {
        if (annotation.is_polygon && annotation.points) {
            const matrix = annotation.calcTransformMatrix();
            const out = [];
            for (let i = 0; i < annotation.points.length; i += 1) {
                const p = fabric.util.transformPoint(
                    new fabric.Point(annotation.points[i].x, annotation.points[i].y),
                    matrix
                );
                out.push(Math.max(0, Math.min(1, p.x / this.canvasWidth)));
                out.push(Math.max(0, Math.min(1, p.y / this.canvasHeight)));
            }
            return out;
        }
        let x = (annotation.left + this.boxBorder) / this.canvasWidth;
        let y = (annotation.top + this.boxBorder) / this.canvasHeight;
        if(annotation.is_point) {
            x = (annotation.left + this.point_radius) / this.canvasWidth;
            y = (annotation.top + this.point_radius)  / this.canvasHeight;
            return [x,y]
        }
        let w = (annotation.width * annotation.scaleX) / this.canvasWidth;
        let h = (annotation.height * annotation.scaleY) / this.canvasHeight;
        return [x,y,w,h]
    },

    getBboxes() {
        let objects = this.canvas.getObjects();

        let results = []
        for (let i = 1; i < objects.length; i++) {
            let bbox = objects[i];
            if (bbox.category) {
                let info = [
                    bbox.category,
                    bbox.left / this.bboxScaleX,
                    bbox.top / this.bboxScaleY,
                    (bbox.width * bbox.scaleX) / this.bboxScaleX,
                    (bbox.height * bbox.scaleY) / this.bboxScaleY];
                results.push(info)
            }
        }

        return results;
    },

    clearBboxes() {
        let objects = this.canvas.getObjects();
        for (let i = 0; i < objects.length; i++) {
            if (objects[i].category) {
                this.deleteObject(false, objects[i]);
            }
        }
        document.querySelector('#num_bboxes').html(0);
    },
};
